// Admin MFA verify (Phase 5c). Shown when password / email-token login
// returns requiresMfa=true (i.e. the admin is already enrolled). The
// successful verify re-signs the cookie with mfa-ok=true and lands the
// admin on /.

import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@progress/kendo-react-buttons';
import type { AdminMfaVerifyRequest } from '@shared/api-types';
import { ApiError, adminApiFetch } from '../../lib/adminApiClient';
import { useAdminAuth } from '../../lib/adminAuth';

export function MfaVerify() {
  const navigate = useNavigate();
  const { refresh } = useAdminAuth();
  const [code, setCode] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setLoading(true);
    try {
      const body: AdminMfaVerifyRequest = { code };
      await adminApiFetch<void>('/api/admin/auth/mfa/verify', { method: 'POST', body });
      await refresh();
      navigate('/', { replace: true });
    } catch (err) {
      if (err instanceof ApiError && err.status === 401) {
        setError('That code is invalid.');
      } else {
        setError('Verification failed.');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={panelStyle}>
      <h2>Enter your TOTP code</h2>
      <p style={{ color: '#475569' }}>Open your authenticator app and enter the current 6-digit code.</p>
      <form onSubmit={onSubmit} style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
        <input
          inputMode="numeric"
          maxLength={6}
          value={code}
          onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
          style={{ padding: 10, fontSize: 18, letterSpacing: '0.3em', textAlign: 'center', border: '1px solid #cbd5e1', borderRadius: 8 }}
          data-testid="mfa-verify-input"
        />
        {error && <div style={{ color: '#dc2626', fontSize: 13 }} role="alert">{error}</div>}
        <Button
          themeColor="primary"
          type="submit"
          disabled={loading || code.length !== 6}
          data-testid="mfa-verify-submit"
        >
          {loading ? 'Verifying…' : 'Verify'}
        </Button>
      </form>
    </div>
  );
}

const panelStyle: React.CSSProperties = {
  maxWidth: 420,
  margin: '40px auto',
  padding: 32,
  background: '#fff',
  borderRadius: 12,
  boxShadow: '0 10px 24px rgba(15, 23, 42, 0.08)',
};
